<template>
  <box-node borderColor="#B2921D" v-if="show">
    <div class="d-flex flex-column gap-2">
      <span class="font-weight-bold">Responda as perguntas obrigatórias</span>
      <div class="d-flex flex-row align-center gap-2">
        <span class="font-size-14">As perguntas obrigatórias permitem criar o relatório e a LME automaticamente.</span>
        <v-btn v-if="!opened" class="new-btn" color="primary" text @click="toggle">Ver perguntas obrigatórias</v-btn>
      </div>
      <v-expand-transition>
        <div v-if="opened">
          <div class="d-flex flex-column gap-2">
            <div class="d-flex flex-column gap-2 divide-y">
              <required-question-group
                v-for="(content, title) in groupedQuestions"
                :key="title"
                :title="title"
                v-bind="isArray(content) ? { questions: content, isLeaf: true } : { groups: content }"
                class="px-4 py-2"
              />
            </div>

            <v-btn class="new-btn align-self-start" color="primary" text @click="toggle">
              Ocultar perguntas obrigatórias
            </v-btn>
          </div>
        </div>
      </v-expand-transition>
    </div>
  </box-node>
</template>

<script>
import { Computed } from '@/_typedefs'
import BoxNode from '@/components/NewAttendanceForm/FormNodes/Box/BoxNode.vue'
import { get, isEmpty, set, isArray } from 'lodash'
import RequiredQuestionGroup from './RequiredQuestionGroup.vue'
import { ToWords } from 'to-words'

import { createNamespacedHelpers } from 'vuex'
const { mapGetters: newAttendanceFormGetters } = createNamespacedHelpers('newAttendanceForm')

const toWords = new ToWords({
  localeCode: 'pt-BR',
  converterOptions: {},
})

export default {
  components: { BoxNode, RequiredQuestionGroup },
  props: {
    requiredQuestions: {
      type: Object,
      required: true,
    },
    requiredParameters: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      unansweredQuestions: {},
      unansweredParameters: false,
      unwatch: null,
      opened: false,
    }
  },
  computed: {
    ...newAttendanceFormGetters(['questions']),
    /** @type {Computed<Record<string, object>>} */
    groupedQuestions() {
      const groups = Object.entries(this.unansweredQuestions).reduce((acc, [key, context]) => {
        if (!context) {
          return acc
        }

        const group = get(acc, context, [])
        const question = this.questions[key]
        group.push({ title: question.title, key: question.questionKey, phase: context.at(0) })

        return set(acc, context, group)
      }, {})

      if (this.unansweredParameters) {
        const requiredParametersValue = Object.values(this.requiredParameters)
        const lengthAsWord = toWords.convert(requiredParametersValue.length).toLocaleLowerCase()

        set(
          groups,
          [`Parâmetro - Deve ser preenchido ao menos um dos ${lengthAsWord} parâmetros`],
          requiredParametersValue.map((key) => ({ title: key, key, phase: 'Parâmetro' })),
        )
      }

      return groups
    },
    /** @type {Computed<boolean>} */
    show() {
      return !isEmpty(this.unansweredQuestions) || this.unansweredParameters
    },
  },
  methods: {
    toggle() {
      this.opened = !this.opened
    },
    isArray,
  },
  created() {
    const answers = this.$store.getters['newAttendanceForm/answers']
    const parameters = this.$store.getters['newAttendanceForm/parameters']

    this.unansweredQuestions = Object.fromEntries(
      Object.entries(this.requiredQuestions).filter(([key, _context]) => isEmpty(answers[key])),
    )

    this.unansweredParameters = Object.keys(this.requiredParameters)
      .map((requiredParameter) => parameters[requiredParameter])
      .every(isEmpty)

    this.unwatch = this.$store.subscribe((mutation, state) => {
      if (mutation.type === 'newAttendanceForm/updateAnswer') {
        const { questionKey, value } = mutation.payload

        if (questionKey in this.requiredQuestions && questionKey in this.unansweredQuestions && !isEmpty(value)) {
          this.$delete(this.unansweredQuestions, questionKey)
          return
        }

        if (questionKey in this.requiredQuestions && !(questionKey in this.unansweredQuestions) && isEmpty(value)) {
          this.$set(this.unansweredQuestions, questionKey, this.requiredQuestions[questionKey])
          return
        }
        return
      }

      if (mutation.type === 'newAttendanceForm/updateParameter') {
        const parameters = state.newAttendanceForm.parameters

        this.unansweredParameters = Object.entries(parameters)
          .filter(([parameter, _value]) => parameter in this.requiredParameters)
          .every(([_parameter, value]) => isEmpty(value))

        return
      }
    })
  },
  beforeDestroy() {
    this.unwatch?.()
  },
}
</script>

<style></style>
